import React, { useEffect, useMemo, useState } from "react";
import { Alert, Card, Checkbox, Input, Radio, Space, Spin, Tag, Typography, message } from "antd";
import { CheckCircleOutlined, LockOutlined } from "@ant-design/icons";
import { useParams } from "react-router-dom";
import nodeAPI from "../../service/ApiServiceNode";
import { Button } from "../../design-system";
import logoImage from "../../assets/logo-mediatropy-2024-name.gif";
import "./survey.scss";

const { Paragraph, Text, Title } = Typography;
const RATING_SCALE = Array.from({ length: 11 }, (_, score) => score);

const isOtherOption = (value) => String(value || "").trim().toLowerCase().startsWith("other");
const getOtherAnswerKey = (questionId) => `${questionId}__other_text`;

const getApiErrorMessage = (error, fallback) => (
    error?.response?.data?.data?.error || fallback
);

const isAlreadySubmittedError = (error, errorMessage) => (
    error?.response?.status === 409
    || /already (?:been )?used|already submitted/i.test(String(errorMessage || ""))
);

const clearSurveyTokenFromUrl = () => {
    if (typeof window !== "undefined") {
        window.history.replaceState(null, "", window.location.pathname);
    }
};

const getTokenFromHash = () => {
    if (typeof window === "undefined") return "";
    return new URLSearchParams(window.location.hash.replace(/^#/, "")).get("token") || "";
};

const getOptions = (question) => {
    if (Array.isArray(question?.options_json)) return question.options_json;
    if (typeof question?.options_json !== "string") return [];

    try {
        const parsed = JSON.parse(question.options_json);
        return Array.isArray(parsed) ? parsed : [];
    } catch (_error) {
        return [];
    }
};

const getChoiceParts = (option) => {
    const normalized = String(option || "").trim();
    const match = normalized.match(/^(\p{Extended_Pictographic}(?:\uFE0F|\u200D\p{Extended_Pictographic})*)\s+(.*)$/u);

    return match ? { emoji: match[1], label: match[2] } : { emoji: "", label: normalized };
};

const getMaxSelections = (question) => {
    const settings = question?.settings_json && typeof question.settings_json === "object"
        ? question.settings_json
        : {};
    const value = parseInt(settings.max_selections, 10);
    return Number.isInteger(value) && value > 0 ? value : null;
};

const AnonymousSurveyPage = ({ previewMode = false }) => {
    const { surveyId } = useParams();
    const [token] = useState(getTokenFromHash);
    const [survey, setSurvey] = useState(null);
    const [answers, setAnswers] = useState({});
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [alreadySubmitted, setAlreadySubmitted] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [invalidQuestionIds, setInvalidQuestionIds] = useState([]);

    useEffect(() => {
        // Keep the bearer token in memory only. Removing the fragment before
        // the request starts avoids browser history/referrer/telemetry copies.
        if (!previewMode) {
            clearSurveyTokenFromUrl();
        }
    }, [previewMode]);

    useEffect(() => {
        const loadSurvey = async () => {
            try {
                if (previewMode) {
                    if (!surveyId) {
                        setErrorMessage("Survey preview not found.");
                        return;
                    }

                    const response = await nodeAPI.get(`/survey/admin/${surveyId}/anonymous-preview`);
                    const previewSurvey = response.data.data?.survey || null;
                    setSurvey(previewSurvey);
                    if (!previewSurvey) {
                        setErrorMessage("Survey preview not found.");
                    }
                    return;
                }

                if (!token) {
                    setErrorMessage("This survey link is invalid or expired.");
                    return;
                }

                const response = await nodeAPI.post("/survey/anonymous/session", { token });
                setSurvey(response.data.data?.survey || null);
            } catch (error) {
                const apiErrorMessage = getApiErrorMessage(
                    error,
                    previewMode ? "Survey preview not found." : "This survey link is invalid or expired."
                );
                if (!previewMode && isAlreadySubmittedError(error, apiErrorMessage)) {
                    setAlreadySubmitted(true);
                    clearSurveyTokenFromUrl();
                } else {
                    setErrorMessage(apiErrorMessage);
                }
            } finally {
                setLoading(false);
            }
        };

        loadSurvey();
    }, [previewMode, surveyId, token]);

    const questions = useMemo(() => survey?.survey_question || [], [survey]);
    const [titlePrimary, ...titleSuffix] = String(survey?.title || "").split(" - ");
    const titleSecondary = titleSuffix.join(" - ");

    const setAnswer = (questionId, value) => {
        setAnswers((current) => ({ ...current, [questionId]: value }));
        setInvalidQuestionIds((current) => current.filter((id) => id !== questionId));
    };

    const setOtherText = (questionId, value) => {
        setAnswers((current) => ({ ...current, [getOtherAnswerKey(questionId)]: value }));
        setInvalidQuestionIds((current) => current.filter((id) => id !== questionId));
    };

    const getOtherText = (question) => String(answers[getOtherAnswerKey(question.id)] || "").trim();

    const needsOtherText = (question) => {
        const value = answers[question.id];
        const hasOther = question.question_type === "single_choice"
            ? isOtherOption(value)
            : question.question_type === "multiple_choice"
                && Array.isArray(value)
                && value.some(isOtherOption);
        return hasOther && !getOtherText(question);
    };

    const isAnswered = (question) => {
        const value = answers[question.id];
        if (question.question_type === "rating_0_10") return value !== undefined && value !== null;
        if (question.question_type === "multiple_choice") return Array.isArray(value) && value.length > 0;
        return Boolean(String(value ?? "").trim());
    };

    const buildAnswer = (question) => {
        const value = answers[question.id];
        if (!isAnswered(question)) return null;

        if (question.question_type === "rating_0_10") {
            return { question_id: question.id, numeric_value: value };
        }
        if (question.question_type === "single_choice") {
            return {
                question_id: question.id,
                option_value: value,
                ...(getOtherText(question) ? { text_value: getOtherText(question) } : {}),
            };
        }
        if (question.question_type === "multiple_choice") {
            return {
                question_id: question.id,
                json_value: value,
                ...(getOtherText(question) ? { text_value: getOtherText(question) } : {}),
            };
        }
        return { question_id: question.id, text_value: String(value || "").trim() };
    };

    const handleSubmit = async () => {
        if (!survey || submitted || submitting) return;

        const missing = questions.filter((question) => question.is_required && !isAnswered(question));
        const missingOther = questions.filter(needsOtherText);
        const invalid = [...new Set([...missing, ...missingOther].map((question) => question.id))];
        if (invalid.length > 0) {
            setInvalidQuestionIds(invalid);
            message.error("Please complete the highlighted questions.");
            return;
        }

        const overLimit = questions.find((question) => {
            const maxSelections = getMaxSelections(question);
            return maxSelections && Array.isArray(answers[question.id]) && answers[question.id].length > maxSelections;
        });
        if (overLimit) {
            setInvalidQuestionIds([overLimit.id]);
            message.error(`Please select up to ${getMaxSelections(overLimit)} options.`);
            return;
        }

        if (previewMode) {
            message.info("Preview only. No response was submitted.");
            return;
        }

        try {
            setSubmitting(true);
            await nodeAPI.post("/survey/anonymous/submit", {
                token,
                answers: questions.map(buildAnswer).filter(Boolean),
            });

            setSubmitted(true);
            setAnswers({});
            clearSurveyTokenFromUrl();
            message.success("Survey submitted. Thank you for your feedback.");
        } catch (error) {
            const apiErrorMessage = getApiErrorMessage(error, "Failed to submit survey.");
            if (isAlreadySubmittedError(error, apiErrorMessage)) {
                setAlreadySubmitted(true);
                clearSurveyTokenFromUrl();
            } else {
                message.error(apiErrorMessage);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const renderQuestion = (question, index) => {
        const value = answers[question.id];
        const invalid = invalidQuestionIds.includes(question.id);
        const options = getOptions(question);

        return (
            <Card key={question.id} className={`anonymous-survey-question-card${invalid ? " anonymous-survey-question-card--invalid" : ""}`}>
                <div className="anonymous-survey-question-content">
                    <div className="anonymous-survey-question-heading">
                        <span className="anonymous-survey-question-index">{String(index + 1).padStart(2, "0")}</span>
                        <div>
                            <span className="anonymous-survey-question-eyebrow">Weekly check-in</span>
                            <div className="anonymous-survey-question-title">
                                <Text strong>{question.question_text}</Text>
                                {question.is_required && <Text type="danger"> *</Text>}
                            </div>
                        </div>
                    </div>

                    {question.helper_text && <Text type="secondary" className="anonymous-survey-question-helper">{question.helper_text}</Text>}

                    {!submitted && question.question_type === "rating_0_10" && (
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(11, minmax(30px, 1fr))", gap: 6 }}>
                            {RATING_SCALE.map((score) => (
                                <button
                                    key={score}
                                    type="button"
                                    onClick={() => setAnswer(question.id, score)}
                                    style={{
                                        minHeight: 38,
                                        borderRadius: 6,
                                        border: value === score ? "1px solid #1677ff" : "1px solid #d9d9d9",
                                        background: value === score ? "#1677ff" : "#fff",
                                        color: value === score ? "#fff" : "#222",
                                    }}
                                >
                                    {score}
                                </button>
                            ))}
                        </div>
                    )}

                    {!submitted && question.question_type === "single_choice" && (
                        <Radio.Group
                            className="anonymous-survey-choice-group"
                            value={value}
                            onChange={(event) => setAnswer(question.id, event.target.value)}
                        >
                            <Space orientation="vertical" className={`anonymous-survey-choice-list${options.length === 3 ? " anonymous-survey-choice-list--three" : ""}`}>
                                {options.map((option) => {
                                    const { emoji, label } = getChoiceParts(option);

                                    return (
                                        <Radio key={option} value={option}>
                                            {emoji && <span className="anonymous-survey-choice-emoji">{emoji}</span>}
                                            <span className="anonymous-survey-choice-label">{label}</span>
                                        </Radio>
                                    );
                                })}
                            </Space>
                        </Radio.Group>
                    )}

                    {!submitted && question.question_type === "multiple_choice" && (
                        <Checkbox.Group
                            className="anonymous-survey-choice-group anonymous-survey-checkbox-group"
                            value={Array.isArray(value) ? value : []}
                            options={options}
                            onChange={(values) => {
                                const maxSelections = getMaxSelections(question);
                                if (maxSelections && values.length > maxSelections) {
                                    message.warning(`Please select up to ${maxSelections} options.`);
                                    return;
                                }
                                setAnswer(question.id, values);
                            }}
                        />
                    )}

                    {!submitted && ["short_text", "long_text"].includes(question.question_type) && (
                        <Input.TextArea
                            className="anonymous-survey-text-input"
                            value={value || ""}
                            rows={question.question_type === "long_text" ? 4 : 2}
                            onChange={(event) => setAnswer(question.id, event.target.value)}
                        />
                    )}

                    {!submitted && (question.question_type === "single_choice"
                        ? isOtherOption(value)
                        : question.question_type === "multiple_choice"
                            && Array.isArray(value)
                            && value.some(isOtherOption)) && (
                        <Input
                            className="anonymous-survey-text-input"
                            value={getOtherText(question)}
                            placeholder="Please provide details for Other"
                            onChange={(event) => setOtherText(question.id, event.target.value)}
                        />
                    )}

                    {invalid && <Text type="danger" className="anonymous-survey-question-error">Please answer this question completely.</Text>}
                </div>
            </Card>
        );
    };

    if (loading) {
        return (
            <div className="anonymous-survey-page anonymous-survey-page--state">
                <div className="anonymous-survey-state-card"><Spin size="large" /></div>
            </div>
        );
    }

    if (alreadySubmitted) {
        return (
            <div className="anonymous-survey-page anonymous-survey-page--state">
                <Card className="anonymous-survey-state-card">
                    <div className="anonymous-survey-state-icon"><CheckCircleOutlined /></div>
                    <Title level={1} className="anonymous-survey-state-title">Already submitted</Title>
                    <Paragraph>
                        Your anonymous response has already been recorded. This survey link cannot be used again.
                    </Paragraph>
                </Card>
            </div>
        );
    }

    if (errorMessage || !survey) {
        return (
            <div className="anonymous-survey-page anonymous-survey-page--state">
                <Card className="anonymous-survey-state-card"><Alert type="error" showIcon title={errorMessage || "Survey not found."} /></Card>
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="anonymous-survey-page anonymous-survey-page--state">
                <Card className="anonymous-survey-state-card">
                    <div className="anonymous-survey-state-icon"><CheckCircleOutlined /></div>
                    <Title level={1} className="anonymous-survey-state-title">Response recorded</Title>
                    <Paragraph>
                        Your response to <Text strong>{survey.title}</Text> was submitted anonymously.
                    </Paragraph>
                    <Paragraph type="secondary">
                        If you open the original link again, it will show that this survey has already been submitted.
                    </Paragraph>
                </Card>
            </div>
        );
    }

    return (
        <div className="anonymous-survey-page">
            <Card className="anonymous-survey-card">
                <div className="anonymous-survey-brand">
                    <img src={logoImage} alt="Mediatropy" />
                    <div className="anonymous-survey-brand-copy">
                        <Title level={1} className="anonymous-survey-title">
                            <span className="anonymous-survey-title-main">{titlePrimary}</span>
                            {titleSecondary && <span className="anonymous-survey-title-period">{titleSecondary}</span>}
                        </Title>
                        <div className="anonymous-survey-kicker">
                            <Tag className="anonymous-survey-tag"><LockOutlined /> Anonymous</Tag>
                        </div>
                    </div>
                </div>
                {previewMode && (
                    <Alert
                        type="info"
                        showIcon
                        message="Preview only"
                        description="This is the anonymous survey view. Your answers will not be submitted."
                    />
                )}
                <div className="anonymous-survey-content">
                    <div className="anonymous-survey-questions">{questions.map(renderQuestion)}</div>
                            <div className="anonymous-survey-actions">
                                <Button className="anonymous-survey-submit" type="primary" onClick={handleSubmit} loading={submitting}>
                                    {previewMode ? "Preview only" : "Submit"}
                                </Button>
                    </div>
                </div>
            </Card>
        </div>
    );
};

export default AnonymousSurveyPage;
